'use client';

import { useRef, useEffect } from 'react';
import { gsap } from '@/lib/gsap';
import { ApplyForm } from '@/components/apply/ApplyForm';

export function ApplyPageClient() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: 0.3 + index * 0.03,
          });
        });

        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1500);
      }

      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          y: 0,
          opacity: 0.5,
          duration: 0.7,
          delay: 0.2,
          ease: 'power3.out',
        });
      }

      // Animate description
      if (descriptionRef.current) {
        gsap.set(descriptionRef.current, { y: 30, opacity: 0 });
        gsap.to(descriptionRef.current, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          delay: 1.2,
          ease: 'power3.out',
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const heroTitle = 'JOIN US';

  return (
    <div ref={heroRef}>
      {/* Hero Section */}
      <section
        className="relative flex min-h-screen flex-col items-center justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Large Title with letter animation */}
        <h1
          ref={titleRef}
          className="font-six-caps text-[calc(1rem+20vw)] leading-[0.85] text-white lowercase text-center"
        >
          {heroTitle.split('').map((char, i) => (
            <span
              key={i}
              className={`letter inline-block ${char === ' ' ? 'mx-4' : ''}`}
              style={{ transformOrigin: 'center bottom' }}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </h1>

        {/* Description */}
        <p
          ref={descriptionRef}
          className="max-w-2xl px-8 text-center text-lg leading-relaxed text-white/60 md:text-xl"
          style={{ marginTop: '60px' }}
        >
          We&apos;re always looking for talented individuals to join our team.
          If you&apos;re passionate about creating exceptional digital experiences, we&apos;d love to hear from you.
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute left-0 right-0"
          style={{
            bottom: 'clamp(2rem, 5vw, 3.5rem)',
            paddingLeft: 'clamp(1.5rem, 8vw, 120px)',
            paddingRight: 'clamp(1.5rem, 8vw, 120px)',
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm tracking-wider text-white/50">Scroll to Apply</span>
              <svg
                className="h-4 w-4 text-white/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            <span className="text-sm tracking-wider text-white/50">
              Start Your Journey
            </span>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: '150px', paddingBottom: '150px' }}>
        <div className="content-padding" style={{ maxWidth: '1440px', margin: '0 auto' }}>
          {/* Section Header */}
          <div className="text-center" style={{ marginBottom: '80px' }}>
            <span className="eyebrow" style={{ marginBottom: '24px' }}>
              Why Datacode
            </span>
            <h2 className="font-six-caps text-[calc(1rem+6vw)] leading-[0.9] text-white lowercase">
              Why Join Our Team?
            </h2>
          </div>

          {/* Benefits Cards - Centered */}
          <div className="flex flex-col md:flex-row items-stretch justify-center gap-6">
            {/* Innovation */}
            <div className="flex flex-col items-center text-center rounded-xl border border-white/10 bg-white/[0.03]" style={{ width: '100%', maxWidth: '320px', padding: '48px 36px', flex: 1 }}>
              <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center" style={{ marginBottom: '24px' }}>
                <svg className="w-6 h-6 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-medium" style={{ marginBottom: '12px' }}>Innovation First</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Real products that ship — web, AI, and mobile — not busywork.
              </p>
            </div>

            {/* Growth */}
            <div className="flex flex-col items-center text-center rounded-xl border border-white/10 bg-white/[0.03]" style={{ width: '100%', maxWidth: '320px', padding: '48px 36px', flex: 1 }}>
              <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center" style={{ marginBottom: '24px' }}>
                <svg className="w-6 h-6 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-medium" style={{ marginBottom: '12px' }}>Career Growth</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Continuous learning and clear paths for advancement.
              </p>
            </div>

            {/* Culture */}
            <div className="flex flex-col items-center text-center rounded-xl border border-white/10 bg-white/[0.03]" style={{ width: '100%', maxWidth: '320px', padding: '48px 36px', flex: 1 }}>
              <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center" style={{ marginBottom: '24px' }}>
                <svg className="w-6 h-6 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-medium" style={{ marginBottom: '12px' }}>Great Culture</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Collaborative environment with talented people.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section style={{ backgroundColor: '#173835', paddingTop: '120px', paddingBottom: '120px' }}>
        <div className="content-padding" style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16 lg:gap-24 items-start">
            {/* Left side - Title */}
            <div className="lg:sticky lg:top-32">
              <span className="eyebrow mb-6">
                Application Form
              </span>
              <h2 className="font-six-caps text-[calc(1rem+8vw)] lg:text-[calc(1rem+5vw)] leading-[0.85] text-white lowercase">
                Apply Now
              </h2>
              <p className="text-white/50 mt-8 text-lg leading-relaxed hidden lg:block">
                Fill out the form and upload your CV. We review every application carefully and will get back to you soon.
              </p>
            </div>

            {/* Right side - Form */}
            <div>
              <ApplyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
